import { makeWidgetProps, asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { isEmpty } from 'lodash';
import type { AwardProps } from 'Components/widgets/tripadvisor-awards/TripAdvisorAwards';
import { TripAdvisorAwards } from 'Components/widgets/tripadvisor-awards/TripAdvisorAwards';
import type { TripAdvisorRatingsProps } from 'Components/widgets/tripadvisor-ratings/TripAdvisorRatings';
import { TripAdvisorRatings } from 'Components/widgets/tripadvisor-ratings/TripAdvisorRatings';
import type { ReviewProps } from 'Components/widgets/tripadvisor-reviews/TripAdvisorReviews';
import { TripAdvisorReviews } from 'Components/widgets/tripadvisor-reviews/TripAdvisorReviews';
type Props = {
  className?: string;
  awards?: Array<AwardProps>;
  awardsLabel?: string;
  ratings?: TripAdvisorRatingsProps;
  ratingsLabel?: string;
  reviews?: Array<ReviewProps>;
  reviewsLabel?: string;
};
export const TripAdvisorNarrow = ({
  awards = [],
  awardsLabel,
  className,
  ratings = makeWidgetProps<TripAdvisorRatingsProps>({}),
  ratingsLabel,
  reviews,
  reviewsLabel,
}: Props) => {
  const [position, setPosition] = useState(0);
  const hasAwards = !isEmpty(awards);
  const hasRatings = !isEmpty(ratings.subratings);
  const hasReviews = !isEmpty(reviews);

  if (!hasAwards && !hasRatings && !hasReviews) {
    return null;
  }

  const items = [];
  const nav = [];

  if (hasReviews) {
    items.push(
      <TripAdvisorReviews
        {...asWidgetProps<ComponentProps<typeof TripAdvisorReviews>>({
          key: 'reviews',
          className: 'w-full',
          reviews: reviews,
          maxLengthOfReview: 150,
        })}
      />,
    );
    nav.push(reviewsLabel);
  }

  if (hasRatings) {
    items.push(
      <TripAdvisorRatings
        {...asWidgetProps<ComponentProps<typeof TripAdvisorRatings>>({
          ...ratings,
          key: 'ratings',
          className: 'w-full',
          subratings: ratings.subratings,
        })}
      />,
    );
    nav.push(ratingsLabel);
  }

  if (hasAwards) {
    items.push(
      <TripAdvisorAwards
        {...asWidgetProps<ComponentProps<typeof TripAdvisorAwards>>({
          key: 'awards',
          className: 'w-full self-center',
          awards: awards,
        })}
      />,
    );
    nav.push(awardsLabel);
  }

  return (
    <section className={classnames('mx-auto max-w-600', className)}>
      <div className="overflow-hidden rounded-12 border-1 border-lightGrey">
        <div
          className="transition-transform flex duration-250 ease-in"
          style={{
            transform: `translate(${position * -100}%, 0)`,
          }}
        >
          {items.map((item, idx) => (
            <div
              aria-hidden={position !== idx}
              className={classnames('flex min-w-full max-w-full overflow-hidden p-10', {
                invisible: position !== idx,
              })}
              key={idx}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
      <div className="relative flex">
        <div
          className="arrow-up arrow-white arrow-bg arrow-bg-sienna absolute border-t-1 border-sienna"
          style={{
            margin: `0 ${(100 / nav.length) * position}%`,
            transition: 'all .25s ease-in .2s',
            width: `${100 / nav.length}%`,
          }}
        />

        {nav.map((button, idx) => (
          <button
            className={classnames(
              'flex-1 py-20 text-center text-14 font-bold',
              {
                'text-black': idx !== position,
              },
              {
                'text-sienna': idx === position,
              },
            )}
            key={button}
            onClick={() => setPosition(idx)}
            type="button"
          >
            <span
              className={classnames('block px-10', {
                'border-s-1': idx > 0,
              })}
            >
              {button}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
};
